// ── 자금 흐름 대시보드 "흐름" 메인 페이지 ────────────────────────────────────
// 권한: CASHFLOW_DASHBOARD_MANAGEMENT
// 월 선택 → 일별 누적 추이 + 항목별 합 + 수기 지출 패널
// 색상은 전역 CSS 토큰(var(--text), var(--bg-card) 등) 사용 — 다크모드 자동 적용.

import React, { useEffect, useState } from 'react';
import { useToast } from '../../components/Toast';
import { useCashflowData } from './cashflow/useCashflowData';
import CumulativeChart from './cashflow/CumulativeChart';
import ItemBreakdown from './cashflow/ItemBreakdown';
import FloorSettingsModal from './cashflow/FloorSettingsModal';
import { todayKstYmd, monthLabel } from './cashflow/constants';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdBlackBtn, stdOutlineBtn,
} from './adminlayout/designStandard';

const MOBILE_BREAKPOINT = 768;

function initialMonth() {
  const ymd = todayKstYmd();
  return { year: Number(ymd.slice(0, 4)), month: Number(ymd.slice(5, 7)) };
}

function shiftMonth({ year, month }, delta) {
  let y = year;
  let m = month + delta;
  while (m < 1) { y -= 1; m += 12; }
  while (m > 12) { y += 1; m -= 12; }
  return { year: y, month: m };
}

export default function CashflowDashboard() {
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [period, setPeriod] = useState(initialMonth());
  const [floorOpen, setFloorOpen] = useState(false);
  const { summary, inflowEntries, outflowEntries, loading, reload } = useCashflowData({
    year: period.year,
    month: period.month,
    showToast,
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={pageStyle}>
      {/* ── 페이지 헤더: 제목/부제(좌) + 월 이동·새로고침(우) — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>흐름</h2>
          <div style={stdPageSubtitle}>자금 흐름 현황을 봅니다.</div>
        </div>
        <div style={actionsRowStyle}>
          <button onClick={() => setPeriod(shiftMonth(period, -1))} style={stdOutlineBtn}>◀</button>
          <span style={monthLabelStyle}>{monthLabel(period.year, period.month)}</span>
          <button onClick={() => setPeriod(shiftMonth(period, +1))} style={stdOutlineBtn}>▶</button>
          <button onClick={reload} style={stdBlackBtn}>새로고침</button>
          <button
            onClick={() => setFloorOpen(true)}
            style={stdOutlineBtn}
            title="흐름 설정 (매입 최소 표시 금액 · 매달 고정 지출)"
            aria-label="흐름 설정"
          >
            ⚙
          </button>
          {loading && <span style={loadingStyle}>로딩 중...</span>}
        </div>
      </div>

      {summary?.ownerMissing && (
        <div style={warnStyle}>
          ⚠️ 기준 사용자 미설정 (app_settings.CASHFLOW_OWNER_USER_ID) — 정산서·상담자배분 항목 0원으로 표시
        </div>
      )}

      <section style={chartCardStyle}>
        <CumulativeChart summary={summary} />
      </section>

      <ItemBreakdown
        summary={summary}
        inflowEntries={inflowEntries}
        outflowEntries={outflowEntries}
        onChanged={reload}
        showToast={showToast}
      />

      <FloorSettingsModal
        open={floorOpen}
        onClose={() => setFloorOpen(false)}
        onSaved={reload}
        showToast={showToast}
      />
    </div>
  );
}

const pageStyle = { maxWidth: 1200, color: 'var(--text)' };
const actionsRowStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const monthLabelStyle = { fontSize: 14, fontWeight: 700, color: 'var(--text)', minWidth: 86, textAlign: 'center' };
const loadingStyle = { fontSize: 13, color: 'var(--text-sub)' };
// 상태 안내 배너 — 얇은 amber 테두리는 상태 표현용으로 유지
const warnStyle = { fontSize: 12, color: 'var(--mm-tone-amber-text)', background: 'var(--mm-tone-amber-bg)', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--mm-tone-amber-border)', marginBottom: 16 };
const chartCardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, boxShadow: 'none', marginBottom: 16 };
